/*************************************************************************
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

class SignalDispatcher {
  constructor() {
    this.reset();
  }

  /**
   * @param {string} eventName - The name of the event to trigger
   * @param {...any} payload - The payload to pass to the observers
   */
  trigger(eventName, ...payload) {
    const observers = this.observerMap[eventName];
    if (eventName !== undefined && observers !== undefined) {
      observers.forEach(observer => {
        observer(...payload);
      });
    }
  }

  /**
   * @param {string} eventName - The name of the event to listen to
   * @param {function} observer - The function to call when the event is triggered
   * @param {Object} options - additional options for the listener
   * @param {AbortSignal} options.signal - an optional AbortSignal to abort the listener
   */
  on(eventName, observer, options = {}) {
    let observers = this.observerMap[eventName];
    if (eventName !== undefined) {
      if (observers === undefined) {
        this.observerMap[eventName] = [];
        observers = this.observerMap[eventName];
      }
      observers.push(observer);

      const { signal } = options;
      if (!signal) {
        return;
      }

      if (
        typeof signal.addEventListener === "function" &&
        typeof signal.removeEventListener === "function"
      ) {
        // If the signal is already aborted, unregister the observer immediately
        if (signal.aborted) {
          this.unregister(eventName, observer);
          return;
        }

        const abortHandler = () => {
          this.unregister(eventName, observer);
        };

        // When the signal is aborted, unregister the observer
        signal.addEventListener("abort", abortHandler, {
          once: true, // call the handler once and remove the listener
          signal: this.observerController.signal
        });
      }
    }
  }

  /**
   * @param {string} eventName - The name of the event to unregister from
   * @param {function} observer - The function to unregister
   */
  unregister(eventName, observer) {
    if (this.observerMap[eventName]) {
      const listeners = this.observerMap[eventName];
      if (listeners.includes(observer)) {
        listeners.splice(listeners.indexOf(observer), 1);
      }
    }
  }

  reset() {
    if (this.observerController) {
      this.observerController.abort();
    }
    this.observerMap = {};
    this.observerController = new AbortController(); // eslint-disable-line no-undef
  }
}

class SignalDispatcherAsync extends SignalDispatcher {
  trigger(eventName, ...payload) {
    return new Promise((resolve, reject) => {
      if (eventName in this.observerMap) {
        const observers = this.observerMap[eventName];
        const promises = observers.map(observer => observer(...payload));
        Promise.all(promises).then(
          response => {
            resolve(response);
          },
          exception => {
            reject(exception);
          }
        );
      } else {
        resolve();
      }
    });
  }
}

export { SignalDispatcher, SignalDispatcherAsync };
